import { computePercentiles, formatMs } from '@/app/pages/Diagnostics/diagnosticsUtils'
import { CommonTable } from '@/components/common-table/CommonTable'
import { Badge } from '@/components/ui/badge'
import type { ApiMetric } from '@/stores/diagnosticsStore'
import { type ColumnDef, createColumnHelper } from '@tanstack/react-table'
import { useMemo } from 'react'

interface SlowestEndpointsProps {
	metrics: ApiMetric[]
	limit?: number
}

interface EndpointStat {
	key: string
	method: string
	pathTemplate: string
	count: number
	p95: number
	avg: number
}

const DYNAMIC_SEGMENT = /^([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}|\d+)$/i

const normalizePath = (url: string): string => {
	try {
		const parsed = new URL(url, window.location.origin)
		const segments = parsed.pathname.split('/').map(seg => (DYNAMIC_SEGMENT.test(seg) ? ':id' : seg))
		return segments.join('/')
	} catch {
		return url
	}
}

const columnHelper = createColumnHelper<EndpointStat>()

const getColumns = (): ColumnDef<EndpointStat>[] => [
	columnHelper.accessor('method', {
		header: 'Method',
		size: 100,
		meta: { fixedWidth: true },
		cell: info => (
			<Badge variant='outline' className='font-mono'>
				{info.getValue()}
			</Badge>
		),
	}) as ColumnDef<EndpointStat>,
	columnHelper.accessor('pathTemplate', {
		header: 'Endpoint',
		size: 200,
		cell: info => (
			<span className='font-mono truncate block' title={info.getValue()}>
				{info.getValue()}
			</span>
		),
	}) as ColumnDef<EndpointStat>,
	columnHelper.accessor('count', {
		header: 'Calls',
		size: 80,
		sortDescFirst: true,
		meta: { fixedWidth: true },
		cell: info => <span className='tabular-nums'>{info.getValue()}</span>,
	}) as ColumnDef<EndpointStat>,
	columnHelper.accessor('avg', {
		header: 'Avg',
		size: 110,
		sortDescFirst: true,
		meta: { fixedWidth: true },
		cell: info => <span className='tabular-nums'>{formatMs(info.getValue())}</span>,
	}) as ColumnDef<EndpointStat>,
	columnHelper.accessor('p95', {
		header: 'p95',
		size: 110,
		sortDescFirst: true,
		meta: { fixedWidth: true },
		cell: info => {
			const value = info.getValue()
			if (value > 1000) {
				return (
					<Badge variant='warning' className='tabular-nums'>
						{formatMs(value)}
					</Badge>
				)
			}
			return <span className='tabular-nums font-medium'>{formatMs(value)}</span>
		},
	}) as ColumnDef<EndpointStat>,
]

export const SlowestEndpoints = ({ metrics, limit = 5 }: SlowestEndpointsProps) => {
	const stats = useMemo<EndpointStat[]>(() => {
		const grouped = new Map<string, { method: string; path: string; durations: number[] }>()
		for (const m of metrics) {
			if (!m.success) {
				continue
			}
			const path = normalizePath(m.url)
			const key = `${m.method} ${path}`
			const entry = grouped.get(key)
			if (entry) {
				entry.durations.push(m.duration)
			} else {
				grouped.set(key, { method: m.method, path, durations: [m.duration] })
			}
		}
		const out: EndpointStat[] = []
		for (const [key, value] of grouped) {
			const { p95 } = computePercentiles(value.durations)
			let sum = 0
			for (const d of value.durations) {
				sum += d
			}
			out.push({
				key,
				method: value.method,
				pathTemplate: value.path,
				count: value.durations.length,
				p95,
				avg: sum / value.durations.length,
			})
		}
		return out.sort((a, b) => b.p95 - a.p95).slice(0, limit)
	}, [metrics, limit])

	const columns = useMemo(() => getColumns(), [])

	if (stats.length === 0) {
		return null
	}

	const rowCount = stats.length
	const headerPx = 40
	const rowPx = 36.5
	const wrapperHeight = Math.ceil(headerPx + rowCount * rowPx + 4)

	return (
		<div className='flex flex-col gap-2 px-4'>
			<div className='text-body text-muted-foreground'>Slowest endpoints (by p95) · Top {limit}</div>
			<div
				className='bg-card shadow overflow-hidden w-full [&_.overflow-auto]:overflow-hidden! [&_.overflow-x-auto]:overflow-hidden!'
				style={{ height: wrapperHeight }}>
				<CommonTable
					dataTestId='slowest-endpoints-table'
					data={stats}
					columns={columns}
					isLoading={false}
					error={null}
					enableSelection={false}
					getRowId={row => row.key}
				/>
			</div>
		</div>
	)
}
